import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Popup, Table, Visibility } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';

import Block from '../../components/CardLayout/components/block';
import CellValue from '../../components/ColumnsValue';
import FacetField from '../../components/FilterComponents';
import Icon from '../../components/CustomIcon';

import {
  clearReport,
  columnsSelector,
  exportProgressSelector,
  getReportRequest,
  progressSelector,
  reportExportToExcelRequest,
  reportPageSelector,
  reportSelector,
  reportTotalCountSelector,
} from '../../ducks/reports';

import { CLAIMS_REPORT_TYPE } from '../../constants/reportType';
import { dateToString } from '../../utils/dateTimeFormater';

import ClaimsReportFilters from './claimsReportFilter';

const ClaimsReport = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [params, setParams] = useState({
    startDate: dateToString(),
    endDate: dateToString(),
    claimNumbers: [],
  });

  const [filter, setFilter] = useState({});
  const [sort, setSort] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isFirstRequest, setIsFirstRequest] = useState(true);

  const columns = useSelector(columnsSelector);
  const report = useSelector(reportSelector);
  const totalCount = useSelector(reportTotalCountSelector);
  const page = useSelector(reportPageSelector);
  const reportProgress = useSelector(progressSelector);
  const exportProgress = useSelector(exportProgressSelector);

  useEffect(() => {
    if (isFirstRequest) {
      dispatch(clearReport());
      setIsFirstRequest(false);
    } else {
      getReport(0);
    }
  }, [filter, sort, searchQuery]);

  const mapData = page => ({
    type: CLAIMS_REPORT_TYPE,
    params: {
      ...params,
      carrier: params.carrier?.value || params.carrier || null,
      filter,
      searchQuery,
      sort,
      page,
    },
  });

  const getReport = page => {
    if (!reportProgress) {
      dispatch(getReportRequest(mapData(page)));
    }
  };

  const handleChangeFilter = (e, { name, value }) => {
    setFilter(prev => {
      const next = { ...prev };
      value ? (next[name] = value) : delete next[name];
      return next;
    });
  };

  const clearFilter = () => setFilter({});

  const exportExcel = () => {
    if (!exportProgress) {
      dispatch(reportExportToExcelRequest(mapData(page)));
    }
  };

  const generateReport = () => {
    setFilter({});
    setSort({});
    getReport(0);
  };

  const nextPage = () => {
    if (report.length < totalCount && !reportProgress) {
      getReport(page + 1);
    }
  };

  const item = {
    menuItem: 'Claims report',
    render: () => (
      <div style={{ position: 'relative' }}>
        <Table compact size="small">
          <Table.Header>
            <Table.Row>
              {columns.map((column, i) => (
                <Table.HeaderCell key={column.name}>
                  <FacetField
                    index={i}
                    gridName={`reports/${CLAIMS_REPORT_TYPE}`}
                    name={column.name}
                    displayNameKey={column.displayNameKey}
                    sort={sort?.name === column.name ? sort.desc : null}
                    setSort={setSort}
                    type={column.type}
                    searchParams={params}
                    value={filter[column.name]}
                    filters={filter}
                    setFilter={handleChangeFilter}
                    source={column.source}
                  />
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {report.map((row, rowIndex) => (
              <Table.Row key={row.id}>
                {columns.map((column, colIndex) => (
                  <Table.Cell key={`${row.id}_${column.name}`}>
                    <CellValue
                      {...column}
                      indexRow={rowIndex}
                      indexColumn={colIndex}
                      value={
                        typeof row[column.name] === 'object'
                          ? row[column.name]?.value
                          : row[column.name]
                      }
                      valueText={
                        typeof row[column.name] === 'object'
                          ? row[column.name]?.name
                          : null
                      }
                      valueTooltip={
                        typeof row[column.name] === 'object'
                          ? row[column.name]?.tooltip
                          : null
                      }
                      rowId={row.id}
                      t={t}
                    />
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        <Visibility once={false} onTopVisible={nextPage} />
      </div>
    ),
    actions: () => [
      <Popup
        content={t('reset_filters')}
        trigger={
          <Button icon onClick={clearFilter} disabled={!Object.keys(filter).length}>
            <Icon name="clear-filter" />
          </Button>
        }
      />,
      <Input
        icon="search"
        size="mini"
        placeholder={t('search_all_fields')}
        value={searchQuery}
        onChange={(e, { value }) => setSearchQuery(value)}
      />,
    ],
  };

  return (
    <div className="container">
      <div className="report">
        <ClaimsReportFilters
          params={params}
          setParams={setParams}
          onGenerateReport={generateReport}
          onExportExcel={exportExcel}
        />

        <div className="report_table-top-padding-450">
          <Block item={item} actions={item.actions} loading={reportProgress} isFullScreen />
        </div>
      </div>
    </div>
  );
};

export default ClaimsReport;
